import { economyConfig } from '../config/economyConfig';
import type { ParkingLot } from '../models/ParkingLot';
import { TILE } from '../models/Tile';
import { clamp } from '../utils/math';
import { ParkingLotEngine } from './ParkingLotEngine';
import { UpgradeEngine } from './UpgradeEngine';

export interface StaffCounts {
  cashier: number;
  security: number;
  cleaner: number;
  manager: number;
  technician: number;
}

export interface StaffEffects {
  cleanliness: number; // 0..100
  security: number; // 0..100
  trafficEfficiency: number; // 0..100
  /** Multiplier on payment time (<1 = faster). */
  paymentTimeFactor: number;
  /** Fraction of maintenance cost saved (0..cap). */
  maintenanceReduction: number;
}

/**
 * StaffEngine (spec section 14): converts a lot's staff into performance
 * effects. Pure and config-driven (all magnitudes from economyConfig).
 */
export const StaffEngine = {
  counts(lot: ParkingLot): StaffCounts {
    const c: StaffCounts = {
      cashier: 0,
      security: 0,
      cleaner: 0,
      manager: 0,
      technician: 0,
    };
    for (const s of lot.staff) {
      if (s.typeId in c) c[s.typeId as keyof StaffCounts]++;
    }
    return c;
  },

  totalSalary(lot: ParkingLot): number {
    let total = 0;
    for (const s of lot.staff) total += s.salary;
    return total;
  },

  /** Managers amplify cleaner/guard effectiveness, capped. */
  managerAmp(counts: StaffCounts): number {
    const E = economyConfig.STAFF_EFFECTS;
    return 1 + Math.min(counts.manager * E.MANAGER_AMP_PER, E.MANAGER_AMP_CAP);
  },

  paymentTimeFactor(lot: ParkingLot): number {
    const E = economyConfig.STAFF_EFFECTS;
    const cashiers = this.counts(lot).cashier;
    const hasBooth = ParkingLotEngine.countTiles(lot, TILE.Cashier) > 0;
    // Upgrades (barrier / QR / LPR) add payment throughput too.
    const upgradeThroughput = UpgradeEngine.aggregate(lot).paymentThroughput;
    const throughput =
      1 +
      cashiers * E.PAY_THROUGHPUT_PER_CASHIER +
      (hasBooth ? E.PAY_THROUGHPUT_CASHIER_TILE : 0) +
      upgradeThroughput;
    return 1 / throughput;
  },

  maintenanceReduction(lot: ParkingLot): number {
    const E = economyConfig.STAFF_EFFECTS;
    const techs = this.counts(lot).technician;
    return Math.min(techs * E.MAINT_REDUCTION_PER_TECH, E.MAINT_REDUCTION_CAP);
  },

  computeEffects(lot: ParkingLot): StaffEffects {
    const E = economyConfig.STAFF_EFFECTS;
    const counts = this.counts(lot);
    const amp = this.managerAmp(counts);
    const capacity = ParkingLotEngine.getCapacity(lot);
    const cctv = ParkingLotEngine.countTiles(lot, TILE.CCTV);

    const sizePenalty =
      Math.floor(capacity / E.CLEAN_SIZE_PENALTY_PER_SPACES) *
      E.CLEAN_SIZE_PENALTY;

    const cleanliness = clamp(
      E.CLEAN_BASE + counts.cleaner * E.CLEAN_PER_CLEANER * amp - sizePenalty,
      0,
      100
    );
    const upgradeSecurity = UpgradeEngine.aggregate(lot).security;
    const security = clamp(
      cctv * E.SEC_PER_CCTV +
        counts.security * E.SEC_PER_GUARD * amp +
        upgradeSecurity,
      0,
      100
    );
    const trafficEfficiency = clamp(
      E.EFF_BASE + counts.manager * E.EFF_PER_MANAGER,
      0,
      100
    );

    return {
      cleanliness,
      security,
      trafficEfficiency,
      paymentTimeFactor: this.paymentTimeFactor(lot),
      maintenanceReduction: this.maintenanceReduction(lot),
    };
  },

  /** Recompute and store the derived quality stats on the lot. */
  applyEffects(lot: ParkingLot): void {
    const e = this.computeEffects(lot);
    lot.cleanliness = e.cleanliness;
    lot.security = e.security;
    lot.trafficEfficiency = e.trafficEfficiency;
  },
};
